import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, CheckCircle2, AlertCircle, ExternalLink, Download } from "lucide-react";
import api, { apiError, downloadCsv } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONNECT_COUNTRIES, guessCountryCode } from "@/lib/connectCountries";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-CA", { month: "long", day: "numeric" });

export const PayoutCard = ({ profile }) => {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [country, setCountry] = useState(guessCountryCode(profile?.country));
  const [busy, setBusy] = useState(false);
  const approved = profile?.status === "approved";

  useEffect(() => {
    if (!approved) return;
    api.get("/connect/status").then((r) => {
      setStatus(r.data);
      if (params.get("connect") === "return") {
        toast.success(r.data.ready ? "Automatic payouts are on ✦" : "Thanks — Stripe is reviewing your details");
      }
    }).catch(() => setStatus({ connected: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, approved]);

  if (!approved) return null;

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/onboard", { country: status?.country || country, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/dashboard-link");
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const ready = status?.ready;
  const connected = status?.connected;

  return (
    <div className={`silk-card rounded-lg p-5 mb-8 ${ready ? "" : "border border-primary/30"}`} data-testid="payout-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {ready ? <CheckCircle2 size={20} className="text-primary mt-0.5 shrink-0" />
            : connected ? <AlertCircle size={20} className="text-foreground/80 mt-0.5 shrink-0" />
            : <Landmark size={20} className="text-primary mt-0.5 shrink-0" />}
          <div className="min-w-0">
            <p className="text-foreground text-sm font-medium">Automatic payouts</p>
            {!status ? (
              <p className="text-xs text-muted-foreground mt-1" data-testid="payout-status">Checking your payout setup…</p>
            ) : ready ? (
              <p className="text-xs text-muted-foreground mt-1" data-testid="payout-status">
                Your 90% share goes straight to your bank on the 1st and 15th.
                {status.pending_count > 0
                  ? <> Next payout <span className="text-foreground">{fmtDate(status.next_run)}</span>: <span className="text-primary">${status.pending_amount} CAD</span> from {status.pending_count} completed session{status.pending_count !== 1 ? "s" : ""}.</>
                  : <> Next payout date: <span className="text-foreground">{fmtDate(status.next_run)}</span> — nothing owing yet.</>}
                {status.upcoming_amount > 0 && <> ${status.upcoming_amount} more from upcoming sessions follows once they've happened.</>}
                {!status.payouts_active && <span className="block text-foreground/80 mt-1" data-testid="payout-bank-hint">Stripe still needs a detail or two before money can reach your bank — finish it anytime from your Stripe dashboard. Your share is held safely in your Stripe balance meanwhile.</span>}
              </p>
            ) : connected ? (
              <p className="text-xs text-foreground/80 mt-1" data-testid="payout-status">
                {status.needs_info ? "Almost there — Stripe needs a few more details before payouts can start." : "Stripe is reviewing your details. Payouts switch on automatically once verified."}
                {" "}Until then your earnings stay safely on your ledger.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1" data-testid="payout-status">
                Get your 90% share sent straight to your bank on the 1st and 15th of every month. Secure setup through Stripe takes about five minutes.
              </p>
            )}
          </div>
        </div>
        {status && (ready ? (
          <div className="flex flex-wrap items-center gap-2">
            {status.has_history && (
              <Button size="sm" variant="ghost" className="rounded-full px-4" data-testid="payout-history-btn"
                onClick={() => downloadCsv("/connect/payouts/history.csv", "orec-payout-history.csv").then(() => toast.success("Payout history downloaded")).catch((e) => toast.error(apiError(e)))}>
                <Download size={13} className="mr-1.5" /> Payout history (CSV)
              </Button>
            )}
            <Button size="sm" variant="outline" className="rounded-full px-5" data-testid="payout-dashboard-btn" disabled={busy} onClick={openDashboard}>
              <ExternalLink size={13} className="mr-1.5" />{busy ? "Opening…" : "Stripe payout dashboard"}
            </Button>
          </div>
        ) : connected ? (
          <Button size="sm" className="rounded-full px-5" data-testid="payout-continue-btn" disabled={busy} onClick={onboard}>
            {busy ? "One moment…" : "Continue Stripe setup"}
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="h-9 w-[170px] text-xs" data-testid="payout-country-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONNECT_COUNTRIES.map(([code, name]) => <SelectItem key={code} value={code}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" className="rounded-full px-5" data-testid="payout-setup-btn" disabled={busy} onClick={onboard}>
              {busy ? "One moment…" : "Set up payouts"}
            </Button>
          </div>
        ))}
      </div>
      {status?.has_history && !ready && (
        <div className="mt-3 flex justify-end">
          <Button size="sm" variant="ghost" className="rounded-full px-4" data-testid="payout-history-btn"
            onClick={() => downloadCsv("/connect/payouts/history.csv", "orec-payout-history.csv").then(() => toast.success("Payout history downloaded")).catch((e) => toast.error(apiError(e)))}>
            <Download size={13} className="mr-1.5" /> Payout history (CSV)
          </Button>
        </div>
      )}
    </div>
  );
};
